import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle, Path } from 'react-native-svg';
import { useQueryClient } from '@tanstack/react-query';
import { goalsApi } from '../../services/apiService';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { commitmentColor } from '../../utils/metricColors';
import { StrategicGoalList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, GoalsSkeleton, SKELETON_DIMENSIONS, ViewOnlyBadge, Breadcrumb, EvidenceTable } from '../../components';
import { DropdownPortal, useDropdownAnchor } from '../../components/DropdownPortal';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, EllipsisVerticalIcon, EditIcon, TrashIcon } from '../../components/Icons';
import { ExportMenuButton } from '../../components/ExportMenuButton';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useGoalsQuery, useDeleteGoalMutation } from '../../hooks/queries';
import { useYear } from '../../contexts/YearContext';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'Goals'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Import (file-input) icon — matches the unified import button across list screens
const FileInputIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Component (matches Figma exactly - 80px)
// ============================================================

interface CircularProgressProps {
  percentage: number | null | undefined;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  textColor?: string;
}

const CircularProgress = ({ percentage, size = 80, strokeWidth = 6, trackColor = tokens.colors.raw.gray["200"], textColor = tokens.colors.semantic.general.foreground }: CircularProgressProps) => {
  // Commitment rate is null when no measurement period is due yet → show "--"
  if (percentage == null) {
    return (
      <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={[styles.progressPercentage, { color: tokens.colors.raw.gray["400"] }]}>--</Text>
      </View>
    );
  }
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Unified commitment color scale (>=80 green / >=50 orange / <50 red)
  const progressColor = commitmentColor(percentage);

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle - gray */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      {/* Percentage text in center */}
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={[styles.progressPercentage, { color: textColor }]}>%{percentage}</Text>
      </View>
    </View>
  );
};

// ============================================================
// Goal Card Component (matches Figma node 2432:365762 exactly)
// ============================================================

interface ThemedCardStyles {
  card: { backgroundColor: string; borderColor: string };
  cardTitle: { color: string };
  cardValue: { color: string };
  cardDescription: { color: string };
  sectionTitle: { color: string };
  pageTitle: { color: string };
  pageSubtitle: { color: string };
  statBox: { backgroundColor: string };
  statLabel: { color: string };
  progressText: { color: string };
  detailsText: { color: string };
  iconColor: string;
  dropdownMenu: { backgroundColor: string; borderColor: string };
  dropdownText: { color: string };
  circleTrack: string;
}

interface GoalCardProps {
  goal: StrategicGoalList;
  onPress: () => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  isOrgEmployee?: boolean;
  isViewOnly?: boolean;
  themedStyles: ThemedCardStyles;
}

const GoalCard = ({ goal, onPress, onEdit, onDelete, isOrgEmployee, isViewOnly, themedStyles }: GoalCardProps) => {
  const menuAnchor = useDropdownAnchor(160);
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const [menuVisible, setMenuVisible] = useState(false);
  // Commitment rate for the card donut; null (nothing due yet) → "--"
  const percentage = goal.commitment_rate ?? null;
  const weight = parseFloat(goal.weight) || 0;

  const handleEdit = () => {
    setMenuVisible(false);
    onEdit?.(goal.id);
  };

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete?.(goal.id);
  };

  return (
    <View style={[styles.goalCard, themedStyles.card, menuVisible && styles.goalCardMenuOpen]}>
      {/* Top row: Progress circle (left) + Content (right) - RTL layout */}
      <View style={styles.goalCardTopRow}>
        {/* Progress circle on the left */}
        <CircularProgress percentage={percentage} size={80} strokeWidth={6} trackColor={themedStyles.circleTrack} textColor={themedStyles.progressText.color} />

        {/* Content on the right */}
        <View style={styles.goalCardContent}>
          {/* Title */}
          <Text style={[styles.goalCardTitle, themedStyles.cardTitle]} numberOfLines={2}>{translate(goal.title)}</Text>

          {/* Description */}
          {goal.description && (
            <Text style={[styles.goalCardDescription, themedStyles.cardDescription]} numberOfLines={2}>
              {translate(goal.description)}
            </Text>
          )}

          {/* Pending approval badge */}
          {goal.status === 'pending' && (
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('goals.pendingApproval')}</Text>
            </View>
          )}
          {/* Pending deletion badge */}
          {goal.has_pending_deletion && <PendingDeletionBadge compact />}
        </View>
      </View>

      {/* Stats row - individual rounded boxes (RTL order: right to left) */}
      <View style={styles.goalCardStatsRow}>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.relativeWeight')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{Math.round(weight)} %</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.kpisCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.kpis_count ?? 0}</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.processesCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.processes_count ?? 0}</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.initiativesCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.initiatives_count ?? 0}</Text>
        </View>
      </View>

      {/* Bottom row: Details link (left) + Menu icon (right) */}
      <View style={styles.goalCardBottomRow}>
        {/* Details link on the left */}
        <TouchableOpacity style={styles.goalCardDetailsLink} onPress={onPress}>
          <ChevronLeftIcon size={16} color={themedStyles.iconColor} />
          <Text style={[styles.goalCardDetailsText, themedStyles.detailsText]}>{t('common.viewDetails')}</Text>
        </TouchableOpacity>

        {/* Menu icon on the right with dropdown - hidden for view-only users (executive) */}
        {!isViewOnly && (
          <View style={styles.menuWrapper} ref={menuAnchor.ref as any} collapsable={false}>
            <TouchableOpacity
              style={styles.goalCardMenu}
              onPress={() => { if (!menuVisible) menuAnchor.measure(); setMenuVisible(!menuVisible); }}
            >
              <EllipsisVerticalIcon size={20} color={themedStyles.iconColor} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Dropdown Menu - portaled to escape stacking contexts */}
      {!isViewOnly && (
        <DropdownPortal
          open={menuVisible}
          onClose={() => setMenuVisible(false)}
          pos={menuAnchor.pos}
          backgroundColor={themedStyles.dropdownMenu.backgroundColor}
          borderColor={themedStyles.dropdownMenu.borderColor}
          maxHeight={200}
        >
          <TouchableOpacity style={styles.dropdownMenuItem} onPress={handleEdit}>
            <Text style={[styles.dropdownMenuItemText, themedStyles.dropdownText]}>{t('common.edit')}</Text>
            <EditIcon size={16} color={themedStyles.iconColor} />
          </TouchableOpacity>
          <View style={[styles.dropdownMenuSeparator, { backgroundColor: themedStyles.dropdownMenu.borderColor }]} />
          <TouchableOpacity style={styles.dropdownMenuItem} onPress={handleDelete}>
            <Text style={styles.dropdownMenuItemTextDelete}>
              {t('common.delete')}
            </Text>
            <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
          </TouchableOpacity>
        </DropdownPortal>
      )}
    </View>
  );
};

// ============================================================
// Pagination Component
// ============================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
      {/* Previous: السابق > */}
      <TouchableOpacity
        style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
        onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Text style={[styles.paginationButtonText, currentPage === 1 && styles.paginationButtonTextDisabled]}>
          {t('common.previous')}
        </Text>
        <ChevronRightIcon size={16} color={currentPage === 1 ? '#A3A3A3' : tokens.colors.semantic.general.foreground} />
      </TouchableOpacity>

      {/* Page numbers */}
      <View style={[styles.paginationPages, { flexDirection: rtlRow }]}>
        {pages.map((page) => (
          <TouchableOpacity
            key={page}
            style={[styles.paginationPage, currentPage === page && styles.paginationPageActive]}
            onPress={() => onPageChange(page)}
          >
            <Text style={[styles.paginationPageText, currentPage === page && styles.paginationPageTextActive]}>
              {page}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Next: < التالي */}
      <TouchableOpacity
        style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
        onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronLeftIcon size={16} color={currentPage === totalPages ? '#A3A3A3' : tokens.colors.semantic.general.foreground} />
        <Text style={[styles.paginationButtonText, currentPage === totalPages && styles.paginationButtonTextDisabled]}>
          {t('common.next')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function GoalsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { navigateToHome, isViewOnly, canSeePending, isDecisionMaker } = useRoleNavigation();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { selectedYear } = useYear();

  // Check if user is org_employee (needs delete approval)
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';

  // Evidence role flags
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = useCanViewAllOrgs();
  const ownDeptId = user?.profile?.organization;

  // Theme-aware styles
  const themedCardStyles: ThemedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageTitle: { color: colors.gray900 },
    pageSubtitle: { color: colors.textMuted },
    statBox: { backgroundColor: colors.backgroundAlt },
    statLabel: { color: colors.textSecondary },
    progressText: { color: colors.textSecondary },
    detailsText: { color: colors.textSecondary },
    iconColor: colors.iconDefault,
    dropdownMenu: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    dropdownText: { color: colors.textSecondary },
    circleTrack: colors.border,
  };

  // Additional themed colors for header, pagination, modal
  const themeColors = {
    exportButton: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    exportButtonText: colors.textPrimary,
    emptyText: colors.textMuted,
    paginationText: colors.textPrimary,
    paginationTextDisabled: colors.textMuted,
    modalBackground: colors.card,
    modalTitle: colors.textPrimary,
    modalMessage: colors.textMuted,
    cancelButton: colors.backgroundAlt,
    cancelButtonText: colors.textPrimary,
  };

  // Responsive breakpoints (maintain compatibility with existing logic)
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;
  const [refreshing, setRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const pageSize = 6;

  const queryClient = useQueryClient();

  const {
    data: goalsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGoalsQuery({
    page: currentPage,
    pageSize: pageSize,
    department_id: effectiveOrgId,
    // Only executive (decision maker) sees approved goals only; org_executive needs pending goals too (to approve them)
    status: isDecisionMaker ? 'approved' : undefined,
    year: selectedYear,
  });

  const goals = goalsData?.results || [];

  // Auto-refresh when screen regains focus (e.g. after add/edit)
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Update total pages when data changes
  React.useEffect(() => {
    if (goalsData?.count) {
      setTotalPages(Math.ceil(goalsData.count / pageSize));
    }
  }, [goalsData?.count, pageSize]);

  // Reset page when org filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteGoal = (id: number) => {
    setGoalToDelete(id);
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    if (!goalToDelete) return;

    try {
      setIsDeleting(true);
      const response = await goalsApi.delete(goalToDelete);

      // Check if it's a delete request (202) or actual delete (204)
      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('goals.deleteSuccess'));
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    } catch (err: any) {
      // Check for 202 response in error (some APIs return 202 as success)
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        queryClient.invalidateQueries({ queryKey: ['goals'] });
      } else {
        showError(err.response?.data?.detail || t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
      setGoalToDelete(null);
    }
  };

  // Handle Excel export - fetch ALL data, not just current page
  const handleExportExcel = async () => {
    if (goalsData?.count === 0) {
      showError(t('common.noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Backend builds the report (goals + KPI sheet + KPI Targets grid), same filters.
      const params: Record<string, any> = { lang: language };
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      if (isDecisionMaker) params.status = 'approved';

      const response = await goalsApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.goals')}.xlsx`);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Results export - same filters as handleExportExcel, results workbook + year-scoped filename
  const handleExportResults = async () => {
    if (goalsData?.count === 0) {
      showError(t('common.noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      const params: Record<string, any> = { lang: language };
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      if (isDecisionMaker) params.status = 'approved';

      const response = await goalsApi.resultsExport(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.goalsResults')}_${selectedYear}.xlsx`);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  // Show skeleton on initial load
  if (isLoading && goals.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={error.message || t('common.loadingError')}
        actionLabel={t('common.refresh')}
        onAction={refetch}
      />
    );
  }

  // Calculate grid columns based on screen width
  const numColumns = isSmallScreen ? 1 : isMediumScreen ? 2 : 2;

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding, minHeight: 600 }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('goals.strategicGoals') },
          ]}
        />

        <PageHeader
          title={t('goals.strategicGoals')}
          actions={
            <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["2"] }}>
              {!isViewOnly && (
                <Button
                  variant="primary"
                  onPress={() => navigation.navigate('AddGoal', {})}
                  trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                >
                  {t('goals.addNewGoal')}
                </Button>
              )}
              {!isViewOnly && (
                <Button
                  variant="secondary"
                  onPress={() => navigation.navigate('ImportGoals' as any)}
                  trailingIcon={<FileInputIcon size={16} color={themeColors.exportButtonText} />}
                >
                  {t('importGoals.title')}
                </Button>
              )}
              <ExportMenuButton
                onExportData={handleExportExcel}
                onExportResults={handleExportResults}
                isExporting={isExporting}
              />
            </View>
          }
          contentTrailing={
            canViewAllOrgs ? (
              <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <OrganizationFilterDropdown
                  selectedOrgId={selectedOrgId}
                  onChange={setSelectedOrgId}
                />
              </View>
            ) : undefined
          }
          style={{ marginBottom: tokens.spacing.absolute["6"] }}
        />

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.emptyText }]}>{t('goals.noStrategicGoals')}</Text>
          </View>
        ) : (
          <View style={[styles.goalsGrid, { flexDirection: isSmallScreen ? 'column' : 'row-reverse', flexWrap: 'wrap' }]}>
            {goals.map((goal) => (
              <View
                key={goal.id}
                style={[
                  styles.goalCardWrapper,
                  {
                    width: isSmallScreen ? '100%' : numColumns === 2 ? '48.5%' : '31.5%',
                  },
                ]}
              >
                <GoalCard
                  goal={goal}
                  onPress={() => navigation.navigate('GoalDetail', { id: goal.id })}
                  onEdit={(id) => navigation.navigate('AddGoal', { goalId: id })}
                  onDelete={(id) => handleDeleteGoal(id)}
                  isOrgEmployee={isOrgEmployee}
                  isViewOnly={isViewOnly}
                  themedStyles={themedCardStyles}
                />
              </View>
            ))}
          </View>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        <EvidenceTable
          relatedType="strategic_goal_collection"
          filterRelatedId={canViewAllEvidence ? undefined : (ownDeptId ?? undefined)}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence && !!ownDeptId}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'strategic_goal_collection',
            relatedId: ownDeptId as number,
            contextLabel: t('goals.strategicGoals'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id,
            relatedType: ev.related_type,
            relatedId: ev.related_id,
            contextLabel: t('goals.strategicGoals'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setGoalToDelete(null); }}
        onConfirm={confirmDelete}
        entityName={goals.find(g => g.id === goalToDelete)?.title || ''}
        fetchImpact={async () => {
          const response = await goalsApi.deletionImpact(goalToDelete!);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles (matching Figma exactly)
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    // Padding set dynamically via inline style for responsiveness
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
    height: SKELETON_DIMENSIONS.BREADCRUMB_HEIGHT,
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Goals Grid
  goalsGrid: {
    gap: tokens.spacing.absolute["5"],
    justifyContent: 'flex-start',
  },
  goalCardWrapper: {
    // Dynamic width set inline
    height: SKELETON_DIMENSIONS.GOAL_CARD_HEIGHT,
  },
  // Goal Card styles (matches Figma node 2432:365762)
  goalCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    position: 'relative',
    zIndex: 1,
    height: SKELETON_DIMENSIONS.GOAL_CARD_HEIGHT,
    overflow: 'hidden', // Prevent content overflow
  },
  goalCardMenuOpen: {
    zIndex: 1000,
  },
  goalCardTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["6"],
  },
  goalCardContent: {
    flex: 1,
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["3"],
  },
  goalCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  goalCardDescription: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  progressPercentage: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  // Pending badge
  pendingBadge: {
    backgroundColor: tokens.colors.raw.amber["100"],
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  pendingBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Stats row - individual boxes (matches Figma exactly)
  goalCardStatsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: tokens.spacing.absolute["4"],
  },
  goalCardStatBox: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["1"],
  },
  goalCardStatLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  goalCardStatValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  // Bottom row
  goalCardBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  menuWrapper: {
    position: 'relative',
  },
  goalCardMenu: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Dropdown menu styles
  dropdownMenu: {
    position: 'absolute',
    bottom: 40,
    end: 8,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.25)',
    elevation: 20,
    minWidth: 160,
    zIndex: 1002,
    paddingVertical: tokens.spacing.absolute["2"],
  },
  dropdownMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
  },
  dropdownMenuItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenuItemTextDelete: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenuSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 1001,
    backgroundColor: 'transparent',
  },
  // Details link
  goalCardDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  goalCardDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Pagination
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["4"],
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    minHeight: 44,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  paginationButtonTextDisabled: {
    color: '#A3A3A3',
  },
  paginationPages: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  paginationPage: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-4"],
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  paginationPageActive: {
    backgroundColor: tokens.colors.semantic.general["Main Color"],
  },
  paginationPageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    color: tokens.colors.semantic.general.foreground,
  },
  paginationPageTextActive: {
    color: tokens.colors.raw.white,
  },
  // Empty state
  emptyContainer: {
    padding: tokens.spacing.absolute["12"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["6"],
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["2"],
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["6"],
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    width: '100%',
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalDeleteButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
});

const styles = getStyles(true);
